import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import { FeatureFlag, FilterReportType } from '@cbo/shared-library';
import {
  tabsSx,
  accordionSx,
  tabPanelSx,
  customReportTabGridSx,
  accordionDetailsSx,
  accordionSummarySx,
  dividerSx,
  selectAllSwitchSx,
  datasetCheckboxAndSwitchSx,
  noSearchResultsSx,
  boldLabelSx,
  searchBarInlineSx,
  selectAllFormControlLabelSx,
  chipColorSx,
  loadingSx,
  customMetricHelperTextSx,
} from '../styles';
import SearchBar from '../../../components/GlobalFilterBar/SearchBar/SearchBar';
import SelectionList from '../../../components/SelectionList/SelectionList';
import { DatasetColumn } from './CreateReportDialog';
import { useGetAllCustomMetricsQuery, useGetCustomReportsDatasetsQuery } from '../../requests/queries';
import { datasetNameMap } from '../utils/EditReportUtils';
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag';

interface Option {
  id: string;
  label: string;
  filterType: FilterReportType | undefined;
}
interface DatasetOption {
  id: string;
  label: string;
  options: Option[];
}

enum DatasetType {
  SALES = 'sales',
  LABOR = 'labor',
}

const normalizeDatasetKey = (value?: string) => (value ?? '').replace(/\s/g, '').toLowerCase();

/**
 * Dialog content for choosing columns to include in a custom report.
 *
 * @param selectedOptions - currently selected dataset columns
 * @param setSelectedOptions - setter to update selection
 * @param isEdit - whether the dialog is opened in edit mode
 * @param selectedDatasetTypeId - optional dataset id when editing (limits options to one dataset)
 * @param hideTabs - when true the tab UI is hidden and all options are shown
 */
function SelectDataset({
  selectedOptions,
  setSelectedOptions,
  isEdit,
  selectedDatasetTypeId = null,
  hideTabs,
}: {
  selectedOptions: DatasetColumn[];
  setSelectedOptions: (opts: DatasetColumn[]) => void;
  isEdit: boolean;
  selectedDatasetTypeId: string | null;
  hideTabs: boolean;
}) {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState<string>(DatasetType.SALES);
  const selectedDatasetType = selectedDatasetTypeId ?? selectedOptions?.[0]?.datasetId;
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { data, isLoading: isDatasetsLoading } = useGetCustomReportsDatasetsQuery();
  const cboHideAlohaEssentialOrgs = useFeatureFlag(FeatureFlag.hideAlohaEssentialOrgs);
  const cboCustomMetrics = useFeatureFlag(FeatureFlag.customMetrics);
  const { data: customMetricsData, isLoading: isCustomMetricsLoading } = useGetAllCustomMetricsQuery(
    0,
    100,
    cboCustomMetrics
  );
  const isLoading = isDatasetsLoading || (cboCustomMetrics && isCustomMetricsLoading);

  const customMetricOptionsByDataset = useMemo(() => {
    if (!cboCustomMetrics) {
      return {} as Record<string, Option[]>;
    }
    const metrics = customMetricsData?.pageContent ?? [];
    return metrics.reduce<Record<string, Option[]>>((acc, metric) => {
      const datasetKey = normalizeDatasetKey(metric.dataset);
      if (!datasetKey) {
        return acc;
      }
      if (!acc[datasetKey]) {
        acc[datasetKey] = [];
      }
      acc[datasetKey].push({
        id: `custommetric_${metric.id}`,
        label: metric.name,
        filterType: undefined,
      });
      return acc;
    }, {});
  }, [customMetricsData, cboCustomMetrics]);

  // Normalize dataset / column API response into grouped select options
  // If in edit mode, only show options from the selected dataset
  const datasetOptions = useMemo(
    () =>
      data
        ? data
            .map((dataset, index) => ({
              id: String(index),
              label: dataset.datasetName,
              options: [...dataset.columns]
                .sort((a, b) => a.columnName.localeCompare(b.columnName, undefined, { sensitivity: 'base' }))
                .map((col) => ({
                  id: `${dataset.datasetName.replace(/\s/g, '').toLowerCase()}_${String(col.id)}`,
                  label: col.columnName,
                  filterType: col.filterType,
                }))
                .concat(customMetricOptionsByDataset[normalizeDatasetKey(dataset.datasetName)] ?? [])
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
            }))
            .filter((ds) => !isEdit || ds.id === selectedDatasetType)
        : [],
    [data, selectedDatasetType, isEdit, customMetricOptionsByDataset]
  );

  // Debounce search input
  const debouncedSearchFn = useMemo(() => debounce((value) => setDebouncedSearch(value?.trim() || ''), 300), []);

  // Cleanup debounce on unmount
  useEffect(() => () => debouncedSearchFn.cancel(), [debouncedSearchFn]);

  // Selection helpers
  // Return true if all options are selected for a dataset
  const isAllSelected = (datasetId: string, allIds: string[]) => {
    const count = selectedOptions.filter(
      (item) => item.datasetId === datasetId || item.datasetId === datasetNameMap[datasetId]
    );
    return count.length === allIds.length;
  };

  // toggle the Select All switch for a dataset
  const toggleSelectAll = (datasetId: string, allOptions: Option[]) => {
    if (
      isAllSelected(
        datasetId,
        allOptions.map((o) => o.id)
      )
    ) {
      // Deselect all
      setSelectedOptions([]);
      return;
    }
    const all = allOptions.map((option) => ({
      datasetId,
      optionId: option.id,
      ...option,
      label: option.label,
      id: `${datasetId}.${option.id}`,
    }));
    setSelectedOptions(all);
  };

  // toggle individual option selection for a dataset
  const toggleOption = (datasetId: string, optionId?: string, optionLabel?: string, filterType?: FilterReportType) => {
    // generate unique ids while formatting data
    const formatted = {
      datasetId,
      optionId: optionId ?? '',
      label: optionLabel ?? '',
      filterType,
      id: `${datasetId}.${optionId}`,
    };
    const exists = selectedOptions.some((item) => item.id === formatted.id);
    setSelectedOptions(
      exists ? selectedOptions.filter((item) => item.id !== formatted.id) : [...selectedOptions, formatted]
    );
  };

  // Filter dataset options by tab & search (memoized)
  const filteredDatasetOptions = useMemo(
    () =>
      datasetOptions
        .filter((ds) => {
          const lbl = ds?.label ? ds.label.toLowerCase() : '';
          if (hideTabs) {
            return true;
          }
          if (tabValue === DatasetType.SALES) {
            return !lbl.includes(DatasetType.LABOR);
          }
          if (tabValue === DatasetType.LABOR) {
            return lbl.includes(DatasetType.LABOR);
          }
          return true;
        })
        .map((ds) => {
          const filteredOptions = ds.options.filter((opt) =>
            opt.label.toLowerCase().includes(debouncedSearch.toLocaleLowerCase())
          );
          return { ...ds, options: filteredOptions };
        })
        .filter((ds) => ds.options.length > 0 || debouncedSearch === ''),
    [datasetOptions, hideTabs, tabValue, debouncedSearch]
  );

  const isDisabled = (datasetId: string) => selectedOptions.length !== 0 && selectedDatasetType !== datasetId;

  // Render dataset options within a tab
  const datasetTabContent = (options: DatasetOption[]) => (
    <Grid container direction='row' justifyContent='space-between'>
      {isLoading ? (
        <CircularProgress sx={loadingSx} aria-label={t('global.loading')} />
      ) : (
        <>
          <Grid item key='left-panel' sm={12} md={6} sx={customReportTabGridSx(theme, isDarkMode, true)}>
            <SearchBar
              sx={searchBarInlineSx}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              showClearIcon={searchValue?.length > 0}
              debouncedSearchFn={debouncedSearchFn}
            />
            {options?.length === 0 && (
              <Box sx={noSearchResultsSx}>{t('admin.centralReportsPage.createReport.noSearchResults')}</Box>
            )}
            {options?.map((dataset, i) => (
              <Box key={dataset.id}>
                {i > 0 && <Divider sx={dividerSx} />}
                <Accordion defaultExpanded sx={accordionSx}>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ ...accordionSummarySx, ...boldLabelSx }}>
                    {dataset.label}
                  </AccordionSummary>
                  <AccordionDetails sx={accordionDetailsSx}>
                    <FormGroup>
                      {debouncedSearch === '' && (
                        <FormControlLabel
                          key={`${dataset.id}-select-all`}
                          control={
                            <Tooltip
                              enterDelay={500}
                              title={
                                isDisabled(dataset.id)
                                  ? t('admin.centralReportsPage.createReport.disabledOptionTooltip')
                                  : ''
                              }
                              placement='right'
                              arrow
                              disableHoverListener={!isDisabled(dataset.id)}
                            >
                              <span>
                                <Switch
                                  checked={isAllSelected(
                                    dataset.id,
                                    dataset.options.map((o) => o.id)
                                  )}
                                  onChange={() =>
                                    toggleSelectAll(
                                      dataset.id,
                                      dataset.options.map((o) => ({
                                        id: o.id,
                                        name: o.label,
                                        label: o.label,
                                        filterType: o.filterType,
                                      }))
                                    )
                                  }
                                  disabled={isDisabled(dataset.id)}
                                  disableRipple
                                  sx={selectAllSwitchSx(theme, isDarkMode)}
                                />
                              </span>
                            </Tooltip>
                          }
                          sx={{ ...selectAllFormControlLabelSx(isDisabled(dataset.id)), ...boldLabelSx }}
                          labelPlacement='start'
                          label={t('admin.centralReportsPage.createReport.selectAllItems')}
                        />
                      )}
                      {dataset.options.map((option) => {
                        const checked = selectedOptions.filter((x) => x.optionId === option.id).length > 0;
                        const isCustomMetric = option.id.startsWith('custommetric_');
                        return (
                          <FormControlLabel
                            key={option.id}
                            control={
                              <Checkbox
                                checked={checked}
                                disabled={isDisabled(dataset.id)}
                                onChange={() => toggleOption(dataset.id, option.id, option.label)}
                                sx={datasetCheckboxAndSwitchSx(theme, isDarkMode)}
                              />
                            }
                            label={
                              <Tooltip
                                enterDelay={500}
                                title={
                                  isDisabled(dataset.id)
                                    ? t('admin.centralReportsPage.createReport.disabledOptionTooltip')
                                    : ''
                                }
                                placement='right'
                                arrow
                                disableHoverListener={!isDisabled(dataset.id)}
                              >
                                <Box>
                                  <Typography variant='body2'>{option.label}</Typography>
                                  {isCustomMetric && (
                                    <Typography variant='caption' sx={customMetricHelperTextSx}>
                                      {t('navItems.customMetricsPage.customMetric')}
                                    </Typography>
                                  )}
                                </Box>
                              </Tooltip>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
          </Grid>
          <Grid item key='right-panel' md={6} sm={12} sx={customReportTabGridSx(theme, isDarkMode, false)}>
            <SelectionList
              selectedItems={selectedOptions.map((opt) => ({
                id: opt.id,
                name: opt.label,
                isCustomMetric: opt.optionId.startsWith('custommetric_'),
              }))}
              onClearSelection={() => setSelectedOptions([])}
              onRemoveItem={(item) => toggleOption(item.split('.')[0], item.split('.')[1], '')}
              getChipColor={(item) => (item.isCustomMetric ? 'success' : 'primary')}
              chipSx={chipColorSx}
              sortByName={false}
            />
          </Grid>
        </>
      )}
    </Grid>
  );

  if (hideTabs) {
    return <Box>{datasetTabContent(filteredDatasetOptions)}</Box>;
  }

  return (
    <TabContext value={tabValue}>
      <Box>
        <TabList
          onChange={(_, newValue: string) => {
            setTabValue(newValue);
          }}
          aria-label='dataset selection tabs'
          sx={tabsSx(theme, isDarkMode)}
        >
          <Tab label={t('admin.centralReportsPage.createReport.sales')} value={DatasetType.SALES} />
          {!cboHideAlohaEssentialOrgs && (
            <Tab label={t('admin.centralReportsPage.createReport.labor')} value={DatasetType.LABOR} />
          )}
        </TabList>
      </Box>
      {Array.from(Array(2).keys()).map((i) => (
        <TabPanel key={i} value={i === 0 ? DatasetType.SALES : DatasetType.LABOR} sx={tabPanelSx}>
          {datasetTabContent(filteredDatasetOptions)}
        </TabPanel>
      ))}
    </TabContext>
  );
}

export default SelectDataset;
